import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useMemo } from "react";
import {
  Plus, DollarSign, Eye, MousePointerClick, TrendingUp, Sparkles, Target, BarChart3,
  Users, Layers, Pause, Play, Copy, Trash2, Archive, Search, Filter, ChevronDown,
  ArrowUpRight, ArrowDownRight, Zap, Award, ShieldCheck, Globe, Smartphone,
  Monitor, Percent, PieChart as PieChartIcon, RefreshCw, CheckCircle, Clock, FileEdit,
  AlertTriangle, ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend, ComposedChart, Line,
  RadialBarChart, RadialBar,
} from "recharts";

/* ─── constants ──────────────────────────────────────── */
const PLATFORM_COLORS: Record<string, string> = {
  facebook: "#1877F2", instagram: "#E4405F", google: "#4285F4",
  tiktok: "#010101", x: "#1DA1F2", linkedin: "#0A66C2", youtube: "#FF0000",
};
const PLATFORM_LABELS: Record<string, string> = {
  facebook: "Facebook", instagram: "Instagram", google: "Google Ads",
  tiktok: "TikTok", x: "X (Twitter)", linkedin: "LinkedIn", youtube: "YouTube",
};
const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600", review: "bg-yellow-100 text-yellow-700",
  active: "bg-green-100 text-green-700", paused: "bg-amber-100 text-amber-700",
  completed: "bg-blue-100 text-blue-700", archived: "bg-gray-200 text-gray-500",
};
const STATUS_ICONS: Record<string, any> = {
  draft: FileEdit, review: Clock, active: CheckCircle, paused: Pause,
  completed: Award, archived: Archive,
};
const OBJECTIVE_LABELS: Record<string, string> = {
  awareness: "Brand Awareness", traffic: "Website Traffic", conversions: "Conversions",
  leads: "Lead Generation", engagement: "Engagement", app_installs: "App Installs",
};
const PERIOD_OPTIONS = [
  { label: "7D", value: "7d" }, { label: "14D", value: "14d" },
  { label: "30D", value: "30d" }, { label: "90D", value: "90d" }, { label: "1Y", value: "1y" },
];
const COLORS = ["#8B5CF6", "#EC4899", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4"];
const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "campaigns", label: "Campaigns", icon: Layers },
  { key: "audiences", label: "Audiences", icon: Users },
  { key: "budget", label: "Budget & ROI", icon: DollarSign },
  { key: "creatives", label: "Creative Lab", icon: Sparkles },
];

const fmt = (n: number, prefix = "") => {
  if (n >= 1e6) return `${prefix}${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${prefix}${(n / 1e3).toFixed(1)}K`;
  return `${prefix}${n.toLocaleString()}`;
};
const fmtMoney = (n: number) => `$${n >= 1e6 ? (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? (n / 1e3).toFixed(1) + "K" : n.toFixed(2)}`;

/* ─── Delta Badge ────────────────────────────────────── */
function Delta({ value }: { value: number }) {
  if (value === 0) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${value > 0 ? "text-green-600" : "text-red-500"}`}>
      {value > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(value)}%
    </span>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function AdManager() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState("30d");
  const [showCreate, setShowCreate] = useState(false);
  const [showAI, setShowAI] = useState(false);



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ad Manager</h1>
          <p className="text-sm text-gray-500 mt-1">Create, optimize, and track ads across all platforms with AI-powered budget optimization</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {PERIOD_OPTIONS.map((p) => (
              <button key={p.value} onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${period === p.value ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={() => setShowAI(true)} className="btn-secondary"><Sparkles className="w-4 h-4" /> AI Ad Copy</button>
          <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus className="w-4 h-4" /> New Campaign</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition ${tab === t.key ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === "overview" && <OverviewTab period={period} />}
      {tab === "campaigns" && <CampaignsTab period={period} onNew={() => setShowCreate(true)} />}
      {tab === "audiences" && <AudiencesTab />}
      {tab === "budget" && <BudgetTab period={period} />}
      {tab === "creatives" && <CreativesTab />}

      {/* Create Campaign Modal */}
      {showCreate && <CreateCampaignModal onClose={() => setShowCreate(false)} />}
      {/* AI Modal */}
      {showAI && <AIAdCopyModal onClose={() => setShowAI(false)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   OVERVIEW TAB
   ═══════════════════════════════════════════════════════ */
function OverviewTab({ period }: { period: string }) {
  const { data: ov } = useQuery({ queryKey: ["ads-overview", period], queryFn: () => api.get<any>(`/ads/overview?period=${period}`) });
  const { data: timeline } = useQuery({ queryKey: ["ads-timeline", period], queryFn: () => api.get<any[]>(`/ads/timeline?period=${period}`) });
  const { data: platforms } = useQuery({ queryKey: ["ads-platforms", period], queryFn: () => api.get<any[]>(`/ads/platforms?period=${period}`) });
  const { data: topPerformers } = useQuery({ queryKey: ["ads-top", period], queryFn: () => api.get<any[]>(`/ads/top-performers?sortBy=roas&limit=5`) });

  if (!ov) return <div className="text-center py-12 text-gray-400">Loading overview…</div>;

  const kpis = [
    { label: "Total Budget", value: fmtMoney(ov.totalBudget), delta: ov.totalBudgetDelta, icon: DollarSign, color: "bg-green-100 text-green-600" },
    { label: "Total Spent", value: fmtMoney(ov.totalSpent), delta: ov.totalSpentDelta, icon: Zap, color: "bg-purple-100 text-purple-600", sub: `${ov.budgetUtilization}% utilized` },
    { label: "Impressions", value: fmt(ov.impressions), delta: ov.impressionsDelta, icon: Eye, color: "bg-blue-100 text-blue-600" },
    { label: "Clicks", value: fmt(ov.clicks), delta: ov.clicksDelta, icon: MousePointerClick, color: "bg-indigo-100 text-indigo-600" },
    { label: "Conversions", value: fmt(ov.conversions), delta: ov.conversionsDelta, icon: Target, color: "bg-emerald-100 text-emerald-600" },
    { label: "Revenue", value: fmtMoney(ov.revenue), delta: ov.revenueDelta, icon: TrendingUp, color: "bg-amber-100 text-amber-600" },
    { label: "CTR", value: `${ov.ctr}%`, delta: ov.ctrDelta, icon: Percent, color: "bg-cyan-100 text-cyan-600" },
    { label: "ROAS", value: `${ov.roas}x`, delta: ov.roasDelta, icon: Award, color: "bg-pink-100 text-pink-600" },
  ];

  const statusData = ov.statusBreakdown ? Object.entries(ov.statusBreakdown).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] })) : [];

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.color}`}><Icon className="w-5 h-5" /></div>
                <Delta value={k.delta} />
              </div>
              <p className="text-2xl font-bold">{k.value}</p>
              <p className="text-sm text-gray-500">{k.label}</p>
              {k.sub && <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>}
            </div>
          );
        })}
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-red-600" /></div>
          <div><p className="text-lg font-bold">{fmtMoney(ov.cpc)}</p><p className="text-xs text-gray-500">Cost Per Click</p><Delta value={ov.cpcDelta} /></div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Eye className="w-5 h-5 text-orange-600" /></div>
          <div><p className="text-lg font-bold">{fmtMoney(ov.cpm)}</p><p className="text-xs text-gray-500">CPM</p><Delta value={ov.cpmDelta} /></div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-teal-600" /></div>
          <div><p className="text-lg font-bold">{fmtMoney(ov.cpa)}</p><p className="text-xs text-gray-500">Cost Per Acquisition</p><Delta value={ov.cpaDelta} /></div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center"><Percent className="w-5 h-5 text-violet-600" /></div>
          <div><p className="text-lg font-bold">{ov.convRate}%</p><p className="text-xs text-gray-500">Conversion Rate</p><Delta value={ov.convRateDelta} /></div>
        </div>
      </div>

      {/* Spend Timeline + Platform Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-6">
          <h3 className="text-lg font-semibold mb-4">Spend & Performance Timeline</h3>
          {timeline?.length ? (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={timeline}>
                <defs>
                  <linearGradient id="spentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
                  formatter={(v: number, name: string) => [name === "spent" || name === "revenue" ? `$${v.toFixed(2)}` : v.toLocaleString(), name]} />
                <Area yAxisId="left" type="monotone" dataKey="spent" fill="url(#spentGrad)" stroke="#8B5CF6" strokeWidth={2} name="Spent" />
                <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={false} name="Revenue" />
                <Bar yAxisId="right" dataKey="clicks" fill="#3B82F640" radius={[2, 2, 0, 0]} name="Clicks" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="text-center py-16 text-gray-400 text-sm">No timeline data</div>}
        </div>

        <div className="card p-6 space-y-6">
          <h3 className="text-lg font-semibold">Platform Distribution</h3>
          {platforms?.length ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={platforms.map((p) => ({ name: PLATFORM_LABELS[p.platform] || p.platform, value: p.spent }))}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                    {platforms.map((p, i) => <Cell key={p.platform} fill={PLATFORM_COLORS[p.platform] || COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => fmtMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {platforms.map((p) => (
                  <div key={p.platform} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[p.platform] }} />
                      <span>{PLATFORM_LABELS[p.platform] || p.platform}</span>
                    </div>
                    <span className="font-medium">{fmtMoney(p.spent)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="text-center py-16 text-gray-400 text-sm">No platform data</div>}

          {/* Status Breakdown */}
          {statusData.length > 0 && (
            <div>
              <h4 className="font-medium text-sm mb-2">Campaign Status</h4>
              <div className="flex flex-wrap gap-2">
                {statusData.map((s) => {
                  const Icon = STATUS_ICONS[s.name] || CheckCircle;
                  return (
                    <span key={s.name} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[s.name] || "bg-gray-100 text-gray-600"}`}>
                      <Icon className="w-3 h-3" />{s.name}: {s.value as number}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Performers */}
      {topPerformers?.length ? (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Top Performers by ROAS</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">ROAS</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Spent</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">CTR</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Quality</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {topPerformers.map((ad: any, i: number) => (
                <tr key={ad.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : "bg-amber-700"}`}>{i + 1}</span>
                      <span className="text-sm font-medium">{ad.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${PLATFORM_COLORS[ad.platform]}15`, color: PLATFORM_COLORS[ad.platform] }}>{PLATFORM_LABELS[ad.platform]}</span></td>
                  <td className="px-4 py-3"><span className="text-sm font-bold text-green-600">{ad.roas?.toFixed(2) || "—"}x</span></td>
                  <td className="px-4 py-3 text-sm">{fmtMoney(ad.spent || 0)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">{fmtMoney(ad.revenue || 0)}</td>
                  <td className="px-4 py-3 text-sm">{ad.ctr?.toFixed(2) || 0}%</td>
                  <td className="px-4 py-3">
                    {ad.qualityScore ? (
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${ad.qualityScore >= 7 ? "bg-green-500" : ad.qualityScore >= 5 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${ad.qualityScore * 10}%` }} />
                        </div>
                        <span className="text-xs font-medium">{ad.qualityScore}/10</span>
                      </div>
                    ) : <span className="text-xs text-gray-400">N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   CAMPAIGNS TAB
   ═══════════════════════════════════════════════════════ */
function CampaignsTab({ period, onNew }: { period: string; onNew: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);

  const params = new URLSearchParams({ page: String(page), limit: "15", search, status: statusFilter, platform: platformFilter, sortBy: "updatedAt", sortDir: "desc" });
  const { data } = useQuery({ queryKey: ["ads-list", page, search, statusFilter, platformFilter], queryFn: () => api.get<any>(`/ads?${params}`) });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/ads/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ads-list"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ads/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ads-list"] }),
  });
  const dupMut = useMutation({
    mutationFn: (id: string) => api.post(`/ads/${id}/duplicate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ads-list"] }),
  });
  const bulkMut = useMutation({
    mutationFn: (body: any) => api.post("/ads/bulk", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ads-list"] }); setSelected([]); },
  });

  const ads = data?.ads || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const allSelected = ads.length > 0 && selected.length === ads.length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9 w-full" placeholder="Search campaigns…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="input w-auto" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="review">In Review</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
        <select className="input w-auto" value={platformFilter} onChange={(e) => { setPlatformFilter(e.target.value); setPage(1); }}>
          <option value="all">All Platforms</option>
          {Object.entries(PLATFORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {selected.length > 0 && (
          <div className="flex gap-2 items-center">
            <span className="text-xs text-gray-500">{selected.length} selected</span>
            <button onClick={() => bulkMut.mutate({ ids: selected, action: "pause" })} className="btn-ghost text-xs"><Pause className="w-3 h-3" /> Pause</button>
            <button onClick={() => bulkMut.mutate({ ids: selected, action: "resume" })} className="btn-ghost text-xs"><Play className="w-3 h-3" /> Resume</button>
            <button onClick={() => bulkMut.mutate({ ids: selected, action: "archive" })} className="btn-ghost text-xs"><Archive className="w-3 h-3" /> Archive</button>
          </div>
        )}
        <button onClick={onNew} className="btn-primary text-sm"><Plus className="w-4 h-4" /> New</button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : ads.map((a: any) => a.id))} className="rounded" />
              </th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Platform</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Budget</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Spent</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Impressions</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">CTR</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">ROAS</th>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ads.length === 0 ? (
              <tr><td colSpan={10} className="px-6 py-12 text-center text-sm text-gray-400">No campaigns found. Create your first ad campaign.</td></tr>
            ) : ads.map((ad: any) => {
              const StatusIcon = STATUS_ICONS[ad.status] || CheckCircle;
              const pacing = ad.totalBudget > 0 ? Math.min(100, (ad.spent / ad.totalBudget) * 100) : 0;
              return (
                <tr key={ad.id} className="hover:bg-gray-50 group">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.includes(ad.id)} onChange={() => setSelected((s) => s.includes(ad.id) ? s.filter((x) => x !== ad.id) : [...s, ad.id])} className="rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{ad.name}</p>
                      <p className="text-xs text-gray-400">{OBJECTIVE_LABELS[ad.objective] || ad.objective} · {ad._count?.adCreatives || 0} creatives</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${PLATFORM_COLORS[ad.platform]}15`, color: PLATFORM_COLORS[ad.platform] }}>
                      {PLATFORM_LABELS[ad.platform] || ad.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[ad.status] || ""}`}>
                      <StatusIcon className="w-3 h-3" />{ad.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{fmtMoney(ad.totalBudget)}</p>
                      <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1">
                        <div className={`h-full rounded-full ${pacing > 90 ? "bg-red-500" : pacing > 70 ? "bg-amber-500" : "bg-green-500"}`} style={{ width: `${pacing}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{fmtMoney(ad.spent)}</td>
                  <td className="px-4 py-3 text-sm">{fmt(ad.impressions)}</td>
                  <td className="px-4 py-3 text-sm">{ad.ctr?.toFixed(2) || 0}%</td>
                  <td className="px-4 py-3 text-sm font-medium">
                    <span className={ad.roas >= 3 ? "text-green-600" : ad.roas >= 1 ? "text-amber-600" : "text-red-500"}>{ad.roas?.toFixed(2) || "—"}x</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      {ad.status === "active" ? (
                        <button onClick={() => statusMut.mutate({ id: ad.id, status: "paused" })} className="p-1 hover:bg-amber-50 rounded" title="Pause"><Pause className="w-4 h-4 text-amber-500" /></button>
                      ) : ad.status === "paused" || ad.status === "draft" ? (
                        <button onClick={() => statusMut.mutate({ id: ad.id, status: "active" })} className="p-1 hover:bg-green-50 rounded" title="Activate"><Play className="w-4 h-4 text-green-500" /></button>
                      ) : null}
                      <button onClick={() => dupMut.mutate(ad.id)} className="p-1 hover:bg-blue-50 rounded" title="Duplicate"><Copy className="w-4 h-4 text-blue-500" /></button>
                      <button onClick={() => deleteMut.mutate(ad.id)} className="p-1 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Pagination */}
        {pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
            <span className="text-gray-500">Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, total)} of {total}</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn-ghost px-2 py-1"><ChevronLeft className="w-4 h-4" /></button>
              {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={`px-3 py-1 text-xs rounded ${page === p ? "bg-purple-600 text-white" : "btn-ghost"}`}>{p}</button>
              ))}
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="btn-ghost px-2 py-1"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   AUDIENCES TAB
   ═══════════════════════════════════════════════════════ */
function AudiencesTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", type: "custom", estimatedSize: 0, ageMin: 18, ageMax: 65, devices: "all" });

  const { data: audiences } = useQuery({ queryKey: ["ad-audiences"], queryFn: () => api.get<any[]>("/ads/audiences") });
  const createMut = useMutation({
    mutationFn: (body: any) => api.post("/ads/audiences", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ad-audiences"] }); setShowCreate(false); },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ads/audiences/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ad-audiences"] }),
  });

  const typeIcon: Record<string, any> = { custom: Users, lookalike: Copy, retargeting: RefreshCw, interest: Target };
  const typeColor: Record<string, string> = { custom: "bg-blue-100 text-blue-600", lookalike: "bg-purple-100 text-purple-600", retargeting: "bg-amber-100 text-amber-600", interest: "bg-green-100 text-green-600" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Saved Audiences ({audiences?.length || 0})</h3>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm"><Plus className="w-4 h-4" /> Create Audience</button>
      </div>

      {audiences?.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map((a: any) => {
            const TypeIcon = typeIcon[a.type] || Users;
            const interests = a.interests ? JSON.parse(a.interests) : [];
            const locations = a.locations ? JSON.parse(a.locations) : [];
            return (
              <div key={a.id} className="card p-5 space-y-4 hover:shadow-md transition">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeColor[a.type]}`}><TypeIcon className="w-5 h-5" /></div>
                    <div>
                      <h4 className="font-semibold">{a.name}</h4>
                      <p className="text-xs text-gray-400 capitalize">{a.type} audience</p>
                    </div>
                  </div>
                  <button onClick={() => deleteMut.mutate(a.id)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
                {a.description && <p className="text-sm text-gray-500">{a.description}</p>}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold">{fmt(a.estimatedSize)}</p>
                    <p className="text-xs text-gray-500">Est. Reach</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold">{a.ageMin || 18}–{a.ageMax || 65}</p>
                    <p className="text-xs text-gray-500">Age Range</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-lg font-bold">{a._count?.adCampaigns || 0}</p>
                    <p className="text-xs text-gray-500">Campaigns</p>
                  </div>
                </div>
                {interests.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {interests.slice(0, 4).map((i: string) => (
                      <span key={i} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{i}</span>
                    ))}
                    {interests.length > 4 && <span className="text-xs text-gray-400">+{interests.length - 4} more</span>}
                  </div>
                )}
                {locations.length > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Globe className="w-3 h-3" />{locations.join(", ")}
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  {a.devices === "mobile" ? <Smartphone className="w-3 h-3" /> : a.devices === "desktop" ? <Monitor className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                  <span className="capitalize">{a.devices || "All"} devices</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center text-sm text-gray-400">No saved audiences yet. Create one to target your ads more effectively.</div>
      )}

      {/* Create Audience Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">Create Audience</h2>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(form); }} className="space-y-4">
              <div><label className="label">Audience Name *</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              <div><label className="label">Description</label><textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Type</label><select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="custom">Custom</option><option value="lookalike">Lookalike</option><option value="retargeting">Retargeting</option><option value="interest">Interest-based</option></select></div>
                <div><label className="label">Estimated Size</label><input type="number" className="input" min="0" value={form.estimatedSize} onChange={(e) => setForm({ ...form, estimatedSize: Number(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="label">Min Age</label><input type="number" className="input" min="13" max="65" value={form.ageMin} onChange={(e) => setForm({ ...form, ageMin: Number(e.target.value) })} /></div>
                <div><label className="label">Max Age</label><input type="number" className="input" min="18" max="100" value={form.ageMax} onChange={(e) => setForm({ ...form, ageMax: Number(e.target.value) })} /></div>
                <div><label className="label">Devices</label><select className="input" value={form.devices} onChange={(e) => setForm({ ...form, devices: e.target.value })}><option value="all">All</option><option value="mobile">Mobile</option><option value="desktop">Desktop</option></select></div>
              </div>
              <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary" disabled={createMut.isPending}>Create</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   BUDGET & ROI TAB
   ═══════════════════════════════════════════════════════ */
function BudgetTab({ period }: { period: string }) {
  const { data: budget } = useQuery({ queryKey: ["ads-budget"], queryFn: () => api.get<any[]>("/ads/summary/budget") });
  const { data: timeline } = useQuery({ queryKey: ["ads-timeline", period], queryFn: () => api.get<any[]>(`/ads/timeline?period=${period}`) });
  const { data: ov } = useQuery({ queryKey: ["ads-overview", period], queryFn: () => api.get<any>(`/ads/overview?period=${period}`) });

  const totalBudget = budget?.reduce((s, b) => s + b.budget, 0) || 0;
  const totalSpent = budget?.reduce((s, b) => s + b.spent, 0) || 0;
  const totalRevenue = budget?.reduce((s, b) => s + b.revenue, 0) || 0;
  const overallRoas = totalSpent > 0 ? totalRevenue / totalSpent : 0;

  // Cumulative spend for pacing chart
  const pacing = useMemo(() => {
    if (!timeline?.length) return [];
    let cumSpent = 0;
    let cumBudgetLine = 0;
    const dailyBudgetTarget = totalBudget / (timeline.length || 1);
    return timeline.map((t) => {
      cumSpent += t.spent;
      cumBudgetLine += dailyBudgetTarget;
      return { date: t.date, actualSpend: Math.round(cumSpent * 100) / 100, plannedSpend: Math.round(cumBudgetLine * 100) / 100 };
    });
  }, [timeline, totalBudget]);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <p className="text-sm text-gray-500">Total Budget</p>
          <p className="text-2xl font-bold">{fmtMoney(totalBudget)}</p>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-2">
            <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{ width: `${Math.min(100, totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0)}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-1">{totalBudget > 0 ? ((totalSpent / totalBudget) * 100).toFixed(1) : 0}% utilized</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Total Spent</p>
          <p className="text-2xl font-bold text-purple-600">{fmtMoney(totalSpent)}</p>
          <p className="text-xs text-gray-400 mt-1">Remaining: {fmtMoney(totalBudget - totalSpent)}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-2xl font-bold text-green-600">{fmtMoney(totalRevenue)}</p>
          <p className="text-xs text-gray-400 mt-1">Net profit: {fmtMoney(totalRevenue - totalSpent)}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Overall ROAS</p>
          <p className={`text-2xl font-bold ${overallRoas >= 3 ? "text-green-600" : overallRoas >= 1 ? "text-amber-600" : "text-red-500"}`}>{overallRoas.toFixed(2)}x</p>
          <p className="text-xs text-gray-400 mt-1">{overallRoas >= 3 ? "Excellent" : overallRoas >= 2 ? "Good" : overallRoas >= 1 ? "Break-even" : "Needs optimization"}</p>
        </div>
      </div>

      {/* Budget Pacing */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Budget Pacing – Actual vs. Planned</h3>
        {pacing.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={pacing}>
              <defs>
                <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "K" : v}`} />
              <Tooltip formatter={(v: number) => [`$${v.toFixed(2)}`, ""]} />
              <Area type="monotone" dataKey="plannedSpend" stroke="#CBD5E1" strokeDasharray="5 5" fill="none" name="Planned" />
              <Area type="monotone" dataKey="actualSpend" stroke="#8B5CF6" fill="url(#actualGrad)" strokeWidth={2} name="Actual" />
            </AreaChart>
          </ResponsiveContainer>
        ) : <div className="text-center py-16 text-gray-400 text-sm">No pacing data</div>}
      </div>

      {/* Platform Budget / ROAS comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Budget Allocation by Platform</h3>
          {budget?.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={budget} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "K" : v}`} />
                <YAxis type="category" dataKey="platform" tick={{ fontSize: 12 }} tickFormatter={(p) => PLATFORM_LABELS[p] || p} width={100} />
                <Tooltip formatter={(v: number) => fmtMoney(v)} />
                <Bar dataKey="budget" fill="#E2E8F0" name="Budget" radius={[0, 4, 4, 0]} />
                <Bar dataKey="spent" fill="#8B5CF6" name="Spent" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-center py-16 text-gray-400 text-sm">No data</div>}
        </div>
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">ROAS by Platform</h3>
          {budget?.length ? (
            <div className="space-y-4">
              {budget.sort((a, b) => b.avgRoas - a.avgRoas).map((b) => (
                <div key={b.platform} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[b.platform] }} />
                  <span className="text-sm w-24">{PLATFORM_LABELS[b.platform]}</span>
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${b.avgRoas >= 3 ? "bg-green-500" : b.avgRoas >= 1 ? "bg-amber-400" : "bg-red-400"}`}
                      style={{ width: `${Math.min(100, (b.avgRoas / 8) * 100)}%` }} />
                  </div>
                  <span className={`text-sm font-bold w-14 text-right ${b.avgRoas >= 3 ? "text-green-600" : b.avgRoas >= 1 ? "text-amber-600" : "text-red-500"}`}>{b.avgRoas.toFixed(2)}x</span>
                </div>
              ))}
            </div>
          ) : <div className="text-center py-16 text-gray-400 text-sm">No data</div>}
        </div>
      </div>

      {/* Detailed Platform Table */}
      {budget?.length ? (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold">Platform Performance Breakdown</h3></div>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaigns</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Budget</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Spent</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Utilization</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">ROAS</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Avg CTR</th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Avg CPC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {budget.map((b) => (
                <tr key={b.platform} className="hover:bg-gray-50">
                  <td className="px-6 py-3"><span className="text-sm font-medium flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[b.platform] }} />{PLATFORM_LABELS[b.platform]}</span></td>
                  <td className="px-4 py-3 text-sm">{b.campaigns}</td>
                  <td className="px-4 py-3 text-sm">{fmtMoney(b.budget)}</td>
                  <td className="px-4 py-3 text-sm">{fmtMoney(b.spent)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div className={`h-full rounded-full ${b.utilization > 90 ? "bg-red-500" : b.utilization > 70 ? "bg-amber-400" : "bg-green-500"}`} style={{ width: `${Math.min(100, b.utilization)}%` }} />
                      </div>
                      <span className="text-xs">{b.utilization}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-600">{fmtMoney(b.revenue)}</td>
                  <td className="px-4 py-3 text-sm font-bold"><span className={b.avgRoas >= 3 ? "text-green-600" : b.avgRoas >= 1 ? "text-amber-600" : "text-red-500"}>{b.avgRoas}x</span></td>
                  <td className="px-4 py-3 text-sm">{b.avgCtr}%</td>
                  <td className="px-4 py-3 text-sm">{fmtMoney(b.avgCpc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   CREATIVES TAB
   ═══════════════════════════════════════════════════════ */
function CreativesTab() {
  const { data: creatives } = useQuery({ queryKey: ["ad-creatives"], queryFn: () => api.get<any[]>("/ads/creatives") });
  const qc = useQueryClient();
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/ads/creatives/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ad-creatives"] }),
  });

  // Group by campaign
  const grouped = useMemo(() => {
    if (!creatives?.length) return {};
    const map: Record<string, { campaign: any; creatives: any[] }> = {};
    creatives.forEach((c: any) => {
      const key = c.adCampaignId;
      if (!map[key]) map[key] = { campaign: c.adCampaign, creatives: [] };
      map[key].creatives.push(c);
    });
    return map;
  }, [creatives]);

  const entries = Object.entries(grouped);

  // Find best variant per group
  const bestVariant = (list: any[]) => {
    if (!list.length) return null;
    return list.reduce((best, curr) => (curr.ctr || 0) > (best.ctr || 0) ? curr : best, list[0]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Creative Lab · A/B Testing ({creatives?.length || 0} creatives)</h3>
      </div>

      {entries.length ? entries.map(([campaignId, group]) => {
        const winner = bestVariant(group.creatives);
        return (
          <div key={campaignId} className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div>
                <h4 className="font-semibold">{group.campaign?.name || "Unknown Campaign"}</h4>
                <p className="text-xs text-gray-400">{PLATFORM_LABELS[group.campaign?.platform] || group.campaign?.platform} · {group.creatives.length} variants</p>
              </div>
              {winner && (
                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                  <Award className="w-3 h-3" /> Variant {winner.variant} winning
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
              {group.creatives.map((c: any) => {
                const isWinner = c.id === winner?.id;
                return (
                  <div key={c.id} className={`rounded-xl border-2 p-4 space-y-3 ${isWinner ? "border-green-400 bg-green-50/30" : "border-gray-200"}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isWinner ? "bg-green-500 text-white" : "bg-gray-200 text-gray-600"}`}>{c.variant}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${c.type === "video" ? "bg-purple-100 text-purple-600" : c.type === "carousel" ? "bg-blue-100 text-blue-600" : c.type === "image" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600"}`}>{c.type}</span>
                        {isWinner && <Award className="w-4 h-4 text-green-500" />}
                      </div>
                      <button onClick={() => deleteMut.mutate(c.id)} className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {/* Preview */}
                    <div className="bg-gray-100 rounded-lg p-3">
                      {c.headline && <p className="text-sm font-bold text-gray-800">{c.headline}</p>}
                      {c.primaryText && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{c.primaryText}</p>}
                      {c.description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{c.description}</p>}
                      {c.callToAction && <span className="inline-block mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded-full">{c.callToAction}</span>}
                    </div>
                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-sm font-bold">{fmt(c.impressions)}</p>
                        <p className="text-[10px] text-gray-400">Impress.</p>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-sm font-bold">{c.ctr?.toFixed(2) || 0}%</p>
                        <p className="text-[10px] text-gray-400">CTR</p>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-sm font-bold">{c.conversionRate?.toFixed(1) || 0}%</p>
                        <p className="text-[10px] text-gray-400">Conv Rate</p>
                      </div>
                    </div>
                    {/* Spend */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{fmt(c.clicks)} clicks</span>
                      <span>{fmtMoney(c.spent)} spent</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }) : (
        <div className="card p-12 text-center text-sm text-gray-400">No creatives yet. Add creatives to your campaigns to start A/B testing.</div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   CREATE CAMPAIGN MODAL (MULTI-STEP WIZARD)
   ═══════════════════════════════════════════════════════ */
function CreateCampaignModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const { data: audiences } = useQuery({ queryKey: ["ad-audiences"], queryFn: () => api.get<any[]>("/ads/audiences") });
  const [form, setForm] = useState({
    name: "", platform: "facebook", objective: "awareness", adFormat: "single_image",
    totalBudget: "", dailyBudget: "", bidStrategy: "auto", bidAmount: "",
    audienceId: "", notes: "", tags: "",
  });

  const createMut = useMutation({
    mutationFn: (body: any) => api.post("/ads", body),
    onSuccess: () => { qc.invalidateQueries(); onClose(); },
  });

  const steps = [
    { num: 1, label: "Campaign Info" },
    { num: 2, label: "Targeting" },
    { num: 3, label: "Budget & Bidding" },
    { num: 4, label: "Review & Launch" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 text-white px-6 py-4">
          <h2 className="text-xl font-bold">Create Ad Campaign</h2>
          <div className="flex gap-2 mt-3">
            {steps.map((s) => (
              <div key={s.num} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.num ? "bg-white text-purple-600" : "bg-white/20 text-white/60"}`}>{s.num}</div>
                <span className={`text-xs hidden sm:block ${step >= s.num ? "text-white" : "text-white/50"}`}>{s.label}</span>
                {s.num < 4 && <div className={`w-8 h-0.5 ${step > s.num ? "bg-white" : "bg-white/20"}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <div><label className="label">Campaign Name *</label><input className="input" placeholder="e.g. Summer Sale 2026" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Platform *</label>
                  <select className="input" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                    {Object.entries(PLATFORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div><label className="label">Objective</label>
                  <select className="input" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>
                    {Object.entries(OBJECTIVE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="label">Ad Format</label>
                <div className="grid grid-cols-4 gap-2">
                  {["single_image", "carousel", "video", "stories"].map((f) => (
                    <button key={f} type="button" onClick={() => setForm({ ...form, adFormat: f })}
                      className={`p-3 rounded-lg border-2 text-xs text-center font-medium transition ${form.adFormat === f ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 hover:border-gray-300"}`}>
                      {f.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="label">Notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes about this campaign…" /></div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="label">Target Audience</label>
                <select className="input" value={form.audienceId} onChange={(e) => setForm({ ...form, audienceId: e.target.value })}>
                  <option value="">— No saved audience (broad targeting) —</option>
                  {audiences?.map((a: any) => <option key={a.id} value={a.id}>{a.name} ({fmt(a.estimatedSize)} reach)</option>)}
                </select>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                <p className="font-medium mb-1">💡 Targeting Tips</p>
                <ul className="list-disc ml-4 space-y-1 text-xs">
                  <li>Start broad and narrow down based on performance data</li>
                  <li>Lookalike audiences often deliver 20-30% better ROAS</li>
                  <li>Retargeting audiences have 3-5x higher conversion rates</li>
                  <li>Save your best-performing audiences for reuse</li>
                </ul>
              </div>
              <div><label className="label">Tags (comma-separated)</label><input className="input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="summer, promo, 2026" /></div>
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Total Budget ($)</label><input type="number" className="input" min="0" step="0.01" value={form.totalBudget} onChange={(e) => setForm({ ...form, totalBudget: e.target.value })} placeholder="5000" /></div>
                <div><label className="label">Daily Budget ($)</label><input type="number" className="input" min="0" step="0.01" value={form.dailyBudget} onChange={(e) => setForm({ ...form, dailyBudget: e.target.value })} placeholder="167" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Bid Strategy</label>
                  <select className="input" value={form.bidStrategy} onChange={(e) => setForm({ ...form, bidStrategy: e.target.value })}>
                    <option value="auto">Auto (Recommended)</option>
                    <option value="manual_cpc">Manual CPC</option>
                    <option value="target_cpa">Target CPA</option>
                    <option value="target_roas">Target ROAS</option>
                    <option value="maximize_conversions">Maximize Conversions</option>
                  </select>
                </div>
                {form.bidStrategy !== "auto" && (
                  <div><label className="label">Bid Amount ($)</label><input type="number" className="input" min="0" step="0.01" value={form.bidAmount} onChange={(e) => setForm({ ...form, bidAmount: e.target.value })} /></div>
                )}
              </div>
              {form.totalBudget && form.dailyBudget && (
                <div className="bg-purple-50 rounded-lg p-4 text-sm">
                  <p className="font-medium text-purple-700 mb-1">Budget Projection</p>
                  <p className="text-purple-600 text-xs">
                    At ${form.dailyBudget}/day, your campaign will run for ~{Math.ceil(Number(form.totalBudget) / Number(form.dailyBudget))} days.
                    Estimated reach: {fmt(Math.floor(Number(form.totalBudget) / 0.008))} impressions.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4 — Review */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-xs text-gray-500 uppercase">Campaign</span><p className="font-semibold">{form.name || "—"}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Platform</span><p className="font-semibold">{PLATFORM_LABELS[form.platform]}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Objective</span><p className="font-semibold">{OBJECTIVE_LABELS[form.objective]}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Format</span><p className="font-semibold capitalize">{form.adFormat.replace("_", " ")}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Total Budget</span><p className="font-semibold">${form.totalBudget || "0"}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Daily Budget</span><p className="font-semibold">${form.dailyBudget || "0"}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Bid Strategy</span><p className="font-semibold capitalize">{form.bidStrategy.replace("_", " ")}</p></div>
                  <div><span className="text-xs text-gray-500 uppercase">Audience</span><p className="font-semibold">{audiences?.find((a: any) => a.id === form.audienceId)?.name || "Broad"}</p></div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />Campaign will be created in <strong>Draft</strong> status. You can review and activate it later.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
          <button onClick={step === 1 ? onClose : () => setStep(step - 1)} className="btn-secondary">{step === 1 ? "Cancel" : "← Back"}</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} className="btn-primary" disabled={step === 1 && !form.name}>Next →</button>
          ) : (
            <button onClick={() => createMut.mutate({ ...form, totalBudget: Number(form.totalBudget) || 0, dailyBudget: Number(form.dailyBudget) || 0, bidAmount: form.bidAmount ? Number(form.bidAmount) : undefined })}
              disabled={createMut.isPending} className="btn-primary">{createMut.isPending ? "Creating…" : "🚀 Create Campaign"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   AI AD COPY MODAL
   ═══════════════════════════════════════════════════════ */
function AIAdCopyModal({ onClose }: { onClose: () => void }) {
  const [product, setProduct] = useState("");
  const [platform, setPlatform] = useState("facebook");
  const [tone, setTone] = useState("professional");
  const [result, setResult] = useState<any>(null);

  const aiMut = useMutation({
    mutationFn: (body: any) => api.post<any>("/ai/generate/ad", body),
    onSuccess: (data) => setResult(data),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 text-white px-6 py-4">
          <h2 className="text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI Ad Copy Generator</h2>
          <p className="text-sm text-white/70 mt-1">Generate high-converting ad copy tailored to your platform</p>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
          <div><label className="label">Product / Service *</label><input className="input" placeholder="Cloud hosting with 99.9% uptime" value={product} onChange={(e) => setProduct(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Platform</label>
              <select className="input" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {Object.entries(PLATFORM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className="label">Tone</label>
              <select className="input" value={tone} onChange={(e) => setTone(e.target.value)}>
                <option value="professional">Professional</option>
                <option value="casual">Casual & Friendly</option>
                <option value="urgent">Urgent / FOMO</option>
                <option value="inspirational">Inspirational</option>
                <option value="humorous">Humorous</option>
              </select>
            </div>
          </div>
          <button onClick={() => aiMut.mutate({ product, platform, tone })} disabled={!product || aiMut.isPending}
            className="btn-primary w-full">{aiMut.isPending ? "Generating…" : "✨ Generate Ad Copy"}</button>

          {result && (
            <div className="bg-gray-50 rounded-xl p-5 mt-4 space-y-4">
              <div><span className="text-xs text-gray-500 uppercase font-medium">Headline</span><p className="font-bold text-lg mt-1">{result.headline}</p></div>
              <div><span className="text-xs text-gray-500 uppercase font-medium">Primary Text</span><p className="text-sm mt-1">{result.primaryText}</p></div>
              <div><span className="text-xs text-gray-500 uppercase font-medium">Description</span><p className="text-sm text-gray-600 mt-1">{result.description}</p></div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 uppercase font-medium">CTA</span>
                <span className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-full">{result.callToAction}</span>
              </div>
              {result.variations?.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-500 uppercase font-medium">A/B Variations</span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {result.variations.map((v: any, i: number) => (
                      <div key={i} className="bg-white rounded-lg p-3 border border-gray-200">
                        <p className="font-semibold text-sm">{v.headline}</p>
                        <p className="text-xs text-gray-600 mt-1">{v.primaryText}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}
